import React, { useState } from 'react';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { Star, Send, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { feedbackAPI } from '../services/api';
import { getUser } from '../services/auth';
import { useLocalization } from '../context/LocalizationContext';
import { resolveApiErrorMessage } from '../utils/apiErrors';

export const Feedback = () => {
  const { needsCompactFont, t } = useLocalization();
  const fieldLabelClass = 'app-form-label';
  const fieldControlClass = 'app-form-control';
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedbackType, setFeedbackType] = useState('improve');
  const [comment, setComment] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const user = getUser();
  const feedbackTypeOptions = [
    {
      id: 'appreciation',
      label: t('feedback.typeAppreciation'),
      hint: t('feedback.typeAppreciationHint'),
      placeholder: t('feedback.typeAppreciationPlaceholder'),
    },
    {
      id: 'improve',
      label: t('feedback.typeImprove'),
      hint: t('feedback.typeImproveHint'),
      placeholder: t('feedback.typeImprovePlaceholder'),
    },
    {
      id: 'issue',
      label: t('feedback.typeIssue'),
      hint: t('feedback.typeIssueHint'),
      placeholder: t('feedback.typeIssuePlaceholder'),
    },
    {
      id: 'feature',
      label: t('feedback.typeFeature'),
      hint: t('feedback.typeFeatureHint'),
      placeholder: t('feedback.typeFeaturePlaceholder'),
    },
  ];
  const selectedType = feedbackTypeOptions.find((item) => item.id === feedbackType) || feedbackTypeOptions[1];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rating === 0) {
      toast.error(t('feedback.ratingRequired'));
      return;
    }

    if (!comment.trim()) {
      toast.error(t('feedback.feedbackRequired'));
      return;
    }

    setLoading(true);
    try {
      const feedbackData = {
        rating,
        comment: `[${selectedType.label}] ${comment.trim()}`,
        email: email || null,
        mobile: mobile || null,
        customerId: user?.customerId || null,
        sourceUrl: window.location.origin,
        timestamp: new Date().toISOString(),
      };

      await feedbackAPI.submitFeedback(feedbackData);

      toast.success(t('feedback.feedbackSuccess'));
      setSubmitted(true);

      // Reset form after 2 seconds
      setTimeout(() => {
        setRating(0);
        setFeedbackType('improve');
        setComment('');
        setEmail('');
        setMobile('');
        setSubmitted(false);
      }, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      const errorMessage = resolveApiErrorMessage(error, t, t('feedback.feedbackFailed'));
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="app-page-shell max-w-5xl fade-in" data-testid="feedback-page">
        <div className="pointer-events-none absolute -z-10 inset-0 dark:hidden">
          <div className="absolute -top-10 -left-10 h-44 w-44 rounded-full bg-primary-200/35 blur-3xl" />
          <div className="absolute top-28 right-2 h-48 w-48 rounded-full bg-primary-200/30 blur-3xl" />
        </div>
        {/* Header Section */}
        <div>
          <h1
            className={`app-page-heading ${needsCompactFont ? 'app-page-heading-te' : ''}`}
          >
            {t('feedback.title')}
          </h1>
          <p className="app-page-subtitle">
            {t('feedback.subtitle')}
          </p>
        </div>

        {/* Feedback Card */}
        <div className="app-surface app-surface-section">
          {submitted ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="bg-green-100 dark:bg-green-900/20 p-4 rounded-full shadow-sm">
                <Check size={40} className="text-green-600 dark:text-green-400" strokeWidth={2} />
              </div>
              <h2 className="app-panel-title text-center">
                {t('feedback.thankYou')}
              </h2>
              <p className="text-primary-700 dark:text-primary-300 text-center max-w-sm">
                {t('feedback.thankYouNote')}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="app-highlight-surface rounded-[1.5rem] p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <div className="app-icon-chip h-10 w-10 rounded-xl">
                    <Sparkles size={18} strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-primary-900 dark:text-white">
                      {t('feedback.quickPromptTitle')}
                    </p>
                    <p className="mt-1 text-sm leading-6 text-primary-700 dark:text-primary-200">
                      {t('feedback.quickPromptSubtitle')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Star Rating */}
              <div>
                <label className="app-form-label mb-4">
                  {t('feedback.questionStep')} <span className="text-error">*</span>
                </label>
                <div className="flex gap-3 sm:gap-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="transition-transform hover:scale-110 focus:outline-none"
                      aria-label={`${t('feedback.questionStep')} ${star}`}
                    >
                      <Star
                        size={40}
                        className={`transition-colors ${
                          star <= (hoverRating || rating)
                            ? 'fill-primary-500 text-primary-500'
                            : 'text-primary-300 dark:text-primary-600'
                        }`}
                        strokeWidth={1.5}
                      />
                    </button>
                  ))}
                </div>
                {rating > 0 && (
                  <p className="text-sm text-primary-700 dark:text-primary-300 mt-2">
                    {t('feedback.youRated', { count: rating, suffix: rating !== 1 ? 's' : '' })}{' '}
                  </p>
                )}
              </div>

              <div>
                <label className="app-form-label mb-4">
                  {t('feedback.typeQuestion')}
                </label>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {feedbackTypeOptions.map((option) => {
                    const active = feedbackType === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setFeedbackType(option.id)}
                        className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                          active
                            ? 'border-primary-400 bg-primary-50 text-primary-700 shadow-sm dark:border-primary-500/60 dark:bg-primary-900/20 dark:text-primary-300'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-primary-200 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-200 dark:hover:border-primary-500/40 dark:hover:bg-slate-900'
                        }`}
                      >
                        <p className="text-sm font-semibold">{option.label}</p>
                        <p className="mt-1 text-xs leading-5 text-primary-600 dark:text-primary-300">
                          {option.hint}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Comment */}
              <div>
                <label htmlFor="comment" className={fieldLabelClass}>
                  {t('feedback.answerQuestion')} <span className="text-error">*</span>
                </label>
                <textarea
                  id="comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={selectedType.placeholder}
                  className={fieldControlClass}
                  rows="5"
                  maxLength={500}
                />
                <div className="mt-3 rounded-xl border border-primary-200 dark:border-slate-700 bg-primary-50 dark:bg-slate-800/70 px-3 py-2.5 flex items-start justify-between gap-3">
                  <p className="text-xs sm:text-sm text-primary-800 dark:text-white leading-relaxed">
                    {selectedType.hint}
                  </p>
                  <span className="text-xs sm:text-sm font-semibold text-primary-800 dark:text-white whitespace-nowrap">
                    {comment.length}/500
                  </span>
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className={fieldLabelClass}>
                  {t('feedback.emailOptional')}
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className={fieldControlClass}
                />
                <p className="mt-2 text-xs sm:text-sm text-primary-600 dark:text-primary-300">
                  {t('feedback.emailHelp')}
                </p>
              </div>

              {/* Mobile */}
              <div>
                <label htmlFor="mobile" className={fieldLabelClass}>
                  {t('feedback.mobileOptional')}
                </label>
                <input
                  type="tel"
                  id="mobile"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder="+91 XXXXX XXXXX"
                  className={fieldControlClass}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="app-primary-button w-full"
              >
                <Send size={18} strokeWidth={2} />
                {loading ? t('feedback.submitting') : t('feedback.submitFeedback')}
              </button>

              <div className="app-note">
                <p className="text-sm text-primary-900 dark:text-white text-center">
                  {t('feedback.reviewNote')}
                </p>
              </div>
            </form>
          )}
        </div>

        {/* FAQ Section */}
        <div className="app-surface app-surface-section">
          <h2 className="app-panel-title mb-4">{t('feedback.whyMatters')}</h2>
          <ul className="space-y-3 text-primary-800 dark:text-primary-200">
            <li className="flex items-start gap-3">
              <span className="text-primary-500 font-bold mt-1">•</span>
              <span>{t('feedback.matters1')}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary-500 font-bold mt-1">•</span>
              <span>{t('feedback.matters2')}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary-500 font-bold mt-1">•</span>
              <span>{t('feedback.matters3')}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary-500 font-bold mt-1">•</span>
              <span>{t('feedback.matters4')}</span>
            </li>
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Feedback;
